"use client"

import { motion, useMotionValue, useSpring } from "motion/react"
import type { MotionStyle, MotionValue, Variants } from "motion/react"
import { useRef, type ReactNode } from "react"
import {
  ArrowNudgeActiveProvider,
  useArrowNudgeActive,
} from "@/components/motion-ui/arrow-link"
import { cn } from "@/lib/utils"

/** Pointer-tilt wiring for one card. */
export interface PointerTilt {
  /** Callback ref for the tilting element. */
  ref: (element: HTMLElement | null) => void
  /** `rotateX` / `rotateY` springs plus perspective. `undefined` when not interactive. */
  style: MotionStyle | undefined
  /** Pointer-move handler. `undefined` when not interactive. */
  onPointerMove: ((event: React.PointerEvent<HTMLElement>) => void) | undefined
  /** Pointer-leave handler. `undefined` when not interactive. */
  onPointerLeave: (() => void) | undefined
  /** Normalized X position (0 to 1) for spotlight effects */
  mouseX: MotionValue<number>
  /** Normalized Y position (0 to 1) for spotlight effects */
  mouseY: MotionValue<number>
}

/** Pointer-tilt mechanic with subtle, elegant spring physics. */
export function usePointerTilt(maxTilt: number = 4, interactive: boolean = true): PointerTilt {
  const cardRef = useRef<HTMLElement | null>(null)

  const rawRotateX = useMotionValue(0)
  const rawRotateY = useMotionValue(0)
  const mouseX = useMotionValue(0.5)
  const mouseY = useMotionValue(0.5)

  // Smooth, dampened spring for subtle feel
  const springConfig = { stiffness: 280, damping: 28, mass: 0.5 }
  const rotateX = useSpring(rawRotateX, springConfig)
  const rotateY = useSpring(rawRotateY, springConfig)

  const handleMove = (event: React.PointerEvent<HTMLElement>) => {
    if (!interactive) return
    const el = cardRef.current || event.currentTarget
    const rect = el.getBoundingClientRect()
    if (!rect || rect.width === 0 || rect.height === 0) return

    const clientX = event.clientX - rect.left
    const clientY = event.clientY - rect.top

    const xPercent = clientX / rect.width
    const yPercent = clientY / rect.height

    mouseX.set(xPercent)
    mouseY.set(yPercent)

    // Subtle rotation between -maxTilt and +maxTilt
    rawRotateX.set(maxTilt * (0.5 - yPercent) * 2)
    rawRotateY.set(maxTilt * (xPercent - 0.5) * 2)
  }

  const reset = () => {
    rawRotateX.set(0)
    rawRotateY.set(0)
    mouseX.set(0.5)
    mouseY.set(0.5)
  }

  const setRef = (element: HTMLElement | null) => {
    cardRef.current = element
  }

  const style: MotionStyle | undefined = interactive
    ? {
        rotateX,
        rotateY,
        transformPerspective: 1000,
        transformStyle: "preserve-3d" as const,
      }
    : undefined

  return {
    ref: setRef,
    style,
    onPointerMove: interactive ? handleMove : undefined,
    onPointerLeave: interactive ? reset : undefined,
    mouseX,
    mouseY,
  }
}

export interface TiltCardProps {
  /** Entrance variant for the outer element. */
  item?: Variants
  /** Whether the tilt and micro-interaction hooks are live. */
  interactive?: boolean
  /** Maximum pointer-tilt swing, in degrees. */
  maxTilt?: number
  /** Merged onto the outer element. */
  outerClassName?: string
  /** Merged onto the inner tilting element. */
  className?: string
  /** When set, the inner element is a real `<a href>`. */
  href?: string
  /** Anchor `target`. Only meaningful with `href`. */
  target?: string
  /** Anchor `rel`. Only meaningful with `href`. */
  rel?: string
  /** Hover-start callback for the `<div>` form. Ignored when `href` is set. */
  onHoverStart?: () => void
  /** Drives descendant `ArrowNudge` instances on hover/focus. Only meaningful with `href`. */
  whileHover?: string
  /** Reserved for API symmetry with anchor gesture wiring. */
  whileFocus?: string
  /** The card's content. */
  children: ReactNode
}

/** Tile that leans towards the pointer with entrance/tilt split across two elements. */
export function TiltCard({
  item,
  interactive = true,
  maxTilt = 4,
  outerClassName,
  className,
  href,
  target,
  rel,
  onHoverStart,
  whileHover,
  whileFocus,
  children,
}: TiltCardProps): ReactNode {
  const tilt = usePointerTilt(maxTilt, interactive)
  const nudge = useArrowNudgeActive()
  const drivesNudge = interactive && !!whileHover

  const inner = href ? (
    <motion.a
      ref={tilt.ref}
      href={href}
      target={target}
      rel={rel}
      className={cn("block size-full will-change-transform", className)}
      style={tilt.style}
      onPointerMove={tilt.onPointerMove}
      onPointerLeave={() => {
        tilt.onPointerLeave?.()
        if (drivesNudge) nudge.gestureProps.onHoverEnd?.()
      }}
      {...(drivesNudge ? nudge.gestureProps : {})}
      whileHover={drivesNudge ? undefined : interactive ? whileHover : undefined}
      whileFocus={drivesNudge ? undefined : interactive ? whileFocus : undefined}
    >
      {drivesNudge ? (
        <ArrowNudgeActiveProvider active={nudge.active}>{children}</ArrowNudgeActiveProvider>
      ) : (
        children
      )}
    </motion.a>
  ) : (
    <motion.div
      ref={tilt.ref}
      className={cn("size-full will-change-transform", className)}
      style={tilt.style}
      onPointerMove={tilt.onPointerMove}
      onPointerLeave={tilt.onPointerLeave}
      onHoverStart={interactive ? onHoverStart : undefined}
    >
      {children}
    </motion.div>
  )

  return (
    <motion.div
      className={cn("h-full [perspective:1000px]", outerClassName)}
      variants={item}
    >
      {inner}
    </motion.div>
  )
}
